import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useInView } from "./Reveal";
import WorkflowDiagram from "./WorkflowDiagram";

// A small piece of the tool for each category page, playing one realistic job:
// it reads, prepares a draft, and waits for a person to approve. The names,
// amounts and times are invented and the screen is labeled as an example.
const PREVIEWS = {
  "accounting-firms": {
    title: "September close",
    meta: "3 of 40 clients",
    rows: [
      {
        label: "Lakeview Dental",
        detail: "Checking ••4471 is $1,240.18 off the statement",
        chip: ["Read", "Request prepared", "Request sent"],
        selected: true,
      },
      {
        label: "Mill Road Bakery",
        detail: "September bank statement not received",
        chip: ["Read", "Request prepared", "Request sent"],
      },
      {
        label: "Fern & Co. Design",
        detail: "Reconciled. 14 documents attached",
        chip: ["Read", "Pack prepared", "Ready for partner"],
      },
    ],
    draft: {
      kicker: "Draft to Lakeview Dental",
      body: "Hi Dana, we're closing September. The checking account ending 4471 is $1,240.18 off against the statement. Could you send the September statement and any deposits made after the 28th?",
      approve: "Approve and send",
      done: "Approved by you · sent 9:42",
    },
    status: [
      "Reading the ledger, the tracking sheet and the shared inbox…",
      "Prepared at 9:41. Nothing has been sent.",
      "Approved at 9:42. Sent and logged.",
    ],
  },
  "recruiting-agencies": {
    title: "Submission pack",
    meta: "Finance Manager role",
    rows: [
      {
        label: "5+ years in a similar role",
        detail: "Résumé: 8 years, the last 3 as Finance Manager",
        chip: ["Checking", "Met", "Met"],
      },
      {
        label: "Owns month-end close",
        detail: "Interview notes, 12 September",
        chip: ["Checking", "Met", "Met"],
      },
      {
        label: "Salary expectation",
        detail: "Not in the ATS or the inbox",
        chip: ["Checking", "Missing", "Asked"],
        open: true,
        selected: true,
      },
    ],
    draft: {
      kicker: "Draft to the candidate",
      body: "Hi Jordan, before we put you forward for the Finance Manager role, could you confirm your salary expectation? The client's range is $120–135k.",
      approve: "Approve and send",
      done: "Approved by the recruiter · sent 14:05",
    },
    status: [
      "Reading the ATS record, the interview notes and the client's brief…",
      "Pack prepared at 14:03. One item to confirm. Nothing sent.",
      "Approved at 14:05. Sent and logged.",
    ],
  },
  "property-management": {
    title: "Maintenance request",
    meta: "Maple Court · Unit 4B",
    lead: {
      kicker: "Tenant, 8:14",
      text: "“There's water under the kitchen sink again.”",
    },
    rows: [
      {
        label: "Unit history",
        detail: "Same sink repaired on 14 April, $185",
        chip: ["Found", "Repeat issue", "Repeat issue"],
      },
      {
        label: "Vendor",
        detail: "Your usual plumber, free tomorrow 9–11",
        chip: ["Found", "Slot held", "Booked"],
        selected: true,
      },
      {
        label: "Owner limit",
        detail: "Owner approval needed above $300. Estimate $220",
        chip: ["Found", "Within limit", "Within limit"],
      },
    ],
    draft: {
      kicker: "Draft work order",
      body: "Leak under the kitchen sink, repeat issue (last repair 14 April). Plumber tomorrow, 9–11. Estimate $220, within the owner's limit. Tenant gets the time window on approval.",
      approve: "Approve",
      done: "Approved by the manager · tenant notified 8:21",
    },
    status: [
      "Reading the request, the unit's records and the vendor list…",
      "Next step drafted at 8:16. Waiting for a manager.",
      "Approved at 8:21. Work order created and logged.",
    ],
  },
  "agencies-consultancies": {
    title: "New inquiry",
    meta: "Website redesign",
    lead: {
      kicker: "Inquiry, Tuesday 10:02",
      text: "“We need a new website before our March launch. Can you send a proposal?”",
    },
    rows: [
      {
        label: "Client history",
        detail: "Two past projects, both paid on time",
        chip: ["Found", "Found", "Found"],
      },
      {
        label: "Scope",
        detail: "Your Website package, plus a launch page",
        chip: ["Found", "Proposal drafted", "Proposal sent"],
        selected: true,
      },
      {
        label: "Price",
        detail: "$18,400 from your rate card. Over $15,000 needs a partner",
        chip: ["Found", "Waiting for partner", "Approved"],
        open: true,
      },
    ],
    draft: {
      kicker: "Draft proposal summary",
      body: "Website redesign with a launch page. Eight weeks, $18,400 from the current rate card. Start 12 January to meet the March launch. Same payment terms as the last two projects.",
      approve: "Approve and send",
      done: "Approved by the partner · sent 11:30",
    },
    status: [
      "Reading the inquiry, the CRM record and the rate card…",
      "Proposal drafted at 10:06. Waiting for a partner.",
      "Approved at 11:30. Sent, logged, project record created.",
    ],
  },
};

// How long each step stays on screen. The draft holds longest so it can be read.
const HOLD = [2600, 5600, 3600];

function Chip({ text, phase, open }) {
  const tone =
    phase === 0
      ? "bg-black/[0.06] text-black/60"
      : open && phase === 1
        ? "border border-dashed border-black/50 text-black"
        : "bg-black text-white";
  return (
    <span
      className={`inline-flex shrink-0 animate-in fade-in items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium duration-500 sm:text-[11px] ${tone}`}
    >
      {phase === 2 && <Check size={10} aria-hidden="true" />}
      {text}
    </span>
  );
}

export default function AppPreview({ slug, steps }) {
  const data = PREVIEWS[slug];
  const [ref, seen] = useInView(0.3);
  const [phase, setPhase] = useState(1);
  const [moving, setMoving] = useState(false);
  useEffect(() => {
    if (!data || !seen) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setPhase(0);
    setMoving(true);
  }, [data, seen]);
  useEffect(() => {
    if (!moving) return undefined;
    const id = setTimeout(() => setPhase((p) => (p + 1) % 3), HOLD[phase]);
    return () => clearTimeout(id);
  }, [moving, phase]);

  if (!data) return <WorkflowDiagram steps={steps} />;
  const { draft } = data;
  return (
    <div
      ref={ref}
      className="flex h-full w-full flex-col justify-center bg-[#efefed] p-4 sm:p-8 lg:p-10"
      data-testid="app-preview"
    >
      <div className="mx-auto grid w-full max-w-[1240px] items-center gap-4 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-3">
          <p className="text-[10px] uppercase tracking-[0.08em] text-black/45">
            Example screen
          </p>
          <ol className="mt-3 flex gap-1.5 lg:mt-5 lg:flex-col lg:gap-2">
            {steps.map((step, i) => (
              <li
                key={step}
                aria-current={i === phase ? "step" : undefined}
                className={`flex-1 rounded-[14px] px-3 py-2 transition-colors duration-500 lg:flex-none lg:px-4 lg:py-3 ${i === phase ? "bg-black text-white" : "bg-white text-black"}`}
              >
                <span className="block text-[10px] opacity-40">0{i + 1}</span>
                <span className="mt-0.5 block text-[11px] leading-[1.2] tracking-[-0.01em] sm:text-[13px] lg:text-[15px]">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="lg:col-span-9">
          <div className="overflow-hidden rounded-[20px] bg-white shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)]">
            <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-4 py-3 sm:px-5">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex shrink-0 gap-1" aria-hidden="true">
                  <span className="h-2 w-2 rounded-full bg-black/15" />
                  <span className="h-2 w-2 rounded-full bg-black/15" />
                  <span className="h-2 w-2 rounded-full bg-black/15" />
                </span>
                <span className="truncate text-[13px] font-medium tracking-[-0.01em] sm:text-[14px]">
                  {data.title}
                </span>
              </div>
              <span className="shrink-0 text-[11px] text-black/45 sm:text-[12px]">
                {data.meta}
              </span>
            </div>

            <div className="grid sm:grid-cols-12">
              <div className="sm:col-span-7 sm:border-r sm:border-black/[0.06]">
                {data.lead && (
                  <div className="border-b border-black/[0.06] bg-[#fafafa] px-4 py-3 sm:px-5">
                    <p className="text-[10px] uppercase tracking-[0.06em] text-black/40">
                      {data.lead.kicker}
                    </p>
                    <p className="mt-1 text-[14px] leading-[1.3] tracking-[-0.01em] sm:text-[16px]">
                      {data.lead.text}
                    </p>
                  </div>
                )}
                <ul>
                  {data.rows.map((row) => (
                    <li
                      key={row.label}
                      className={`flex items-center justify-between gap-3 border-b border-black/[0.06] px-4 py-2.5 transition-colors duration-500 sm:px-5 sm:py-3 ${row.selected && phase > 0 ? "bg-black/[0.03]" : ""}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] tracking-[-0.01em] sm:text-[14px]">
                          {row.label}
                        </span>
                        <span className="block text-[11px] leading-[1.3] text-black/50 sm:text-[12px]">
                          {row.detail}
                        </span>
                      </span>
                      <Chip
                        key={row.chip[phase]}
                        text={row.chip[phase]}
                        phase={phase}
                        open={row.open}
                      />
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col border-b border-black/[0.06] bg-[#fafafa] px-4 py-3 sm:col-span-5 sm:px-5 sm:py-4">
                <p className="text-[10px] uppercase tracking-[0.06em] text-black/40">
                  {draft.kicker}
                </p>
                {phase === 0 ? (
                  <div className="mt-3 space-y-2" aria-hidden="true">
                    {[92, 100, 84, 60].map((w) => (
                      <span
                        key={w}
                        className="block h-2.5 animate-pulse rounded-full bg-black/[0.07]"
                        style={{ width: `${w}%` }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 animate-in fade-in text-[12px] leading-[1.45] text-black/80 duration-700 sm:text-[13px]">
                    {draft.body}
                  </p>
                )}
                <div className="mt-auto pt-3">
                  {phase === 2 ? (
                    <p className="flex animate-in fade-in items-center gap-1.5 text-[11px] font-medium duration-500 sm:text-[12px]">
                      <Check size={12} aria-hidden="true" />
                      {draft.done}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={phase === 0}
                        onClick={() => setPhase(2)}
                        data-testid="preview-approve"
                        className="rounded-full bg-black px-3.5 py-1.5 text-[11px] font-medium text-white transition-opacity hover:bg-[#262626] disabled:opacity-25 sm:text-[12px]"
                      >
                        {draft.approve}
                      </button>
                      <span className="rounded-full px-3 py-1.5 text-[11px] text-black/50 ring-1 ring-black/10 sm:text-[12px]">
                        Edit
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className={`flex items-center gap-2.5 px-4 py-2.5 text-[11px] transition-colors duration-500 sm:px-5 sm:py-3 sm:text-[13px] ${phase === 0 ? "bg-white text-black/55" : "bg-black text-white"}`}
            >
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${phase === 0 ? "animate-pulse bg-black/40" : "bg-white"}`}
                aria-hidden="true"
              />
              <span key={phase} className="animate-in fade-in duration-500">
                {data.status[phase]}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
